import React, { useEffect, useState, useContext } from "react";
import { MastersChangeLog, saveMastersApi } from "../../../Utility";
import { toast } from "react-toastify";
import { Urls } from "../../../../../Urls";
import { sendMailToMasters } from "../../Utility/utility";
import { UserApiContext } from "../../../../UserDetailsContext/UserDetailsContext";
import { MyCompanyProfileContext } from "../../../../../MyCompanyProfileContext";
import { useMixPanelForTracking } from "../../../../../utils/TrackMixPanel";

/**
 * Step 2 - material Review step
 * Component for displaying and reviewing uploaded material details.
 * 
 * @param {number} step - current step
 * @param {React.SetStateAction} setStep - sets the current step
 * @param {Array<Object>} uploadedData - Array of uploaded route data
 * @param {React.SetStateAction} setUploadedData - setter to update the uploaded data
 * @param {Array<Object>} otherMasterList - List of materials
 * @param {React.SetStateAction} setOtherMasterList - setter to update the materials
 * @param {String} company_id - Company id
 * @returns {JSX.Element} rendered LocationDetails component
 */
const OtherDetails = ({
  setLoading,
  loading,
  superAdmins,
  emailTemplate,
  uploadedData,
  setOtherMasterList,
  otherMasterList,
  company_id,
  selectedOrg,
  fetchMaterialMaster,
}) => {
  const [uploadedDataState, setUploadedDataState] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState("ft");
  const { userDetails } = useContext(UserApiContext);
  const { companySettingData } = useContext(MyCompanyProfileContext);
  const { TrackMixPanel } = useMixPanelForTracking(userDetails, companySettingData);

  const LENGTH_UNITS = [
    { label: "ft (feet)", value: "ft" },
    { label: "in (inches)", value: "in" },
    { label: "m (meters)", value: "m" },
    { label: "cm (centimeters)", value: "cm" },
    { label: "mm (millimeters)", value: "mm" },
    { label: "yd (yards)", value: "yd" },
  ];

  const UNIT_TO_FEET = {
    ft: 1,
    in: 1 / 12,
    m: 3.28084,
    cm: 0.0328084,
    mm: 0.00328084,
    yd: 3,
  };

  const convertToFeet = (value, unit) => {
    const numericValue = parseFloat(value);
    if (isNaN(numericValue)) return 0;
    return Number((numericValue * UNIT_TO_FEET[unit]).toFixed(4));
  };

  const formatDecimal = (value) => {
    return typeof value === "number" ? Number(value.toFixed(2)) : value;
  };

  // useeffect to set uploaded data and keep them in their original units for display
  useEffect(() => {
    if (uploadedData?.length > 0) {
      const formattedData = uploadedData.map((item) => {
        const len = parseFloat(item["Length*"]) || 0;
        const bread = parseFloat(item["Breadth*"]) || 0;
        const height = parseFloat(item["Height*"]) || 0;
        const vol = parseFloat(item["Volume*"]) || Number((len * bread * height).toFixed(4)) || 0;

        return {
          ...item,
          "Length*": len,
          "Breadth*": bread,
          "Height*": height,
          "Volume*": vol,
          "Weight*": formatDecimal(item["Weight*"]),
        };
      });
      setUploadedDataState(formattedData);
    }
  }, [uploadedData]);

  /**
   * Saves the material details and updates the material master.
   * @param {Array<Object>} other_details - Array of location details to be saved
   */
  const saveDetails = async () => {
    setLoading(true)
    const payload = uploadedDataState.map((details) => {
      const lenInFeet = convertToFeet(details["Length*"], selectedUnit);
      const breadInFeet = convertToFeet(details["Breadth*"], selectedUnit);
      const heightInFeet = convertToFeet(details["Height*"], selectedUnit);
      const volInFeet = Number((lenInFeet * breadInFeet * heightInFeet).toFixed(4));

      return {
        material_description: details["Material Description*"],
        material_type: details["Material Type*"],
        material_category: details["Material Category*"],
        material_code: String(details["Material Code*"]),
        length: lenInFeet,
        breadth: breadInFeet,
        height: heightInFeet,
        volume: volInFeet,
        weight: details["Weight*"],
      };
    });



    TrackMixPanel({
      moduleName: 'masters',
      screenName: 'masters - other masters - material master',
      data: 'none',
      definition: 'material master details review: user clicked on confirm and save button',
    });

    try {
      const response = await saveMastersApi(
        company_id,
        "material",
        payload,
        selectedOrg,
        TrackMixPanel, userDetails?.email, userDetails?.user_name
      );

      const activity = {
        total_items: payload.length,
        uploaded_materials: payload.map(item => ({
          material_code: Number(item.material_code),
          updated_data: item
        }))
      }

      if (response.data?.status === false) {
        toast.error(response.data?.message);
        setLoading(false);
        return;
      } else {
        fetchMaterialMaster();

        MastersChangeLog(
          "File Upload",
          "material_master",
          userDetails.email,
          userDetails.user_id,
          company_id,
          activity
        );

        sendMailToMasters(
          "Material Master",
          emailTemplate,
          superAdmins,
          {
            action: "New File Uploaded",
            lane: '',
          },
          setLoading,
          userDetails?.user_name || userDetails?.email?.split("@")[0]
        );
        toast.success("Details Added successfully");
      }

    } catch (err) {
      console.error("Error saving details:", err);
      toast.error("Failed to save details");
    } finally {
      setLoading(false)
    }
  };

  const tableHeaders = [
    "Sr No.",
    "Material Description",
    "Material Type",
    "Material Category",
    "Material Code",
    `Length (${selectedUnit})`,
    `Breadth (${selectedUnit})`,
    `Height (${selectedUnit})`,
    `Volume (${selectedUnit}³)`,
    "Weight (kg)",
  ];

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex align-items-center justify-content-between mb-3 flex-wrap" style={{ gap: "15px" }}>
        <h5 className="mb-0 font-weight-bold" style={{ color: "#393186" }}>
          Check Your Material Details
        </h5>
        <div className="d-flex align-items-center" style={{ gap: "10px" }}>
          <span className="font-weight-bold" style={{ fontSize: "14px", color: "#555" }}>
            Select Uploaded Unit:
          </span>
          <select
            className="form-control"
            style={{ width: "160px", height: "38px" }}
            value={selectedUnit}
            onChange={(e) => setSelectedUnit(e.target.value)}
          >
            {LENGTH_UNITS.map((unit) => (
              <option key={unit.value} value={unit.value}>
                {unit.label}
              </option>
            ))}
          </select>
        </div>
      </div>
      <div
        className="table-responsive"
        style={{ maxHeight: "350px", overflow: "auto" }}
      >
        <table className="table table-striped table-bordered">
          <thead className="thead-light">
            <tr>
              {tableHeaders.map((header, index) => (
                <th key={index} scope="col" className="text-center">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {uploadedDataState.map((data, index) => (
              <tr key={index}>
                <td className="text-center">{index + 1}</td>
                <td className="text-center">{data["Material Description*"]}</td>
                <td className="text-center">{data["Material Type*"]}</td>
                <td className="text-center">{data["Material Category*"]}</td>
                <td className="text-center">{data["Material Code*"]}</td>
                <td className="text-center">{data["Length*"]}</td>
                <td className="text-center">{data["Breadth*"]}</td>
                <td className="text-center">{data["Height*"]}</td>
                <td className="text-center">{data["Volume*"]}</td>
                <td className="text-center">{data["Weight*"]}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="text-center mt-3">
        <button disabled={loading} className="btn btn-success" onClick={saveDetails}>
          Confirm and Save
        </button>
      </div>
    </div>
  );
};

export default OtherDetails;
